/**
 * The single Supabase client for the process.
 *
 * Split into its own module so `auth.tsx` exports only components. Two client instances
 * race each other refreshing the same token and one of them loses, which presents to the
 * user as a random logout — so this must stay a module-level singleton.
 *
 * AUTH ONLY, BUILT DIRECTLY (2026-10-02). The console uses `supabase.auth` and nothing else —
 * there is no `/rest/v1` route in Kong, no storage, no realtime, no functions — but
 * `createClient` constructs all five, and with them it shipped postgrest-js, storage-js,
 * realtime-js, phoenix and functions-js in the ENTRY chunk that every cold load pays before
 * the sign-in screen paints. So the auth client is built here exactly as `createClient` builds
 * it (supabase-js 2.112 `_initSupabaseAuthClient`): the same `auth/v1` URL, the same `apikey`
 * and bearer headers plus the client-info header, and — the one that matters most — the same
 * `sb-<host>-auth-token` storage key, so a session stored by the old client is read by this
 * one and nobody is signed out by the upgrade. `AuthClient` comes through supabase-js's own
 * re-export of auth-js, so no new dependency is added; the unused clients tree-shake away
 * because supabase-js declares its index side-effect free.
 *
 * `supabase` keeps its `{ auth }` shape so every `supabase.auth.*` call site is unchanged.
 */
import { AuthClient, type GoTrueClient } from '@supabase/supabase-js';

export const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL ?? 'http://localhost:8000';
const ANON = import.meta.env.VITE_SUPABASE_ANON_KEY ?? '';

/**
 * Where the session is kept, decided by the operator's "Remember me" choice.
 *
 * WHY THIS IS NOT A COSMETIC CHECKBOX. The default is `localStorage`, which survives the
 * browser closing — so on a shared control-room machine the next person to open the console
 * is signed in as whoever used it last. "Remember me", unchecked, must therefore put the
 * tokens somewhere that dies with the tab, and `sessionStorage` is exactly that.
 *
 * The PREFERENCE itself lives in localStorage; only the TOKENS move. The distinction
 * matters: remembering the choice is harmless, and a person who unticked the box would
 * otherwise have to untick it again every visit — which is how a security affordance gets
 * ignored.
 *
 * Every access is wrapped: private browsing, blocked site data and some enterprise policies
 * make even reading `localStorage` throw, and an exception here happens before sign-in and
 * would take the whole page down.
 */
const REMEMBER_KEY = 'lumen.auth.remember';

export function getRemember(): boolean {
  try {
    /* Default TRUE — the behaviour before this existed, so nobody is silently signed out
       by the upgrade. Only an explicit "false" opts into session-only storage. */
    return window.localStorage.getItem(REMEMBER_KEY) !== 'false';
  } catch {
    return true;
  }
}

export function setRemember(on: boolean): void {
  try {
    window.localStorage.setItem(REMEMBER_KEY, on ? 'true' : 'false');
  } catch {
    /* Preference not remembered; the storage choice below still applies to this session. */
  }
}

/**
 * A storage that delegates to whichever store the preference names, per call.
 *
 * Resolved per call rather than captured once, so ticking the box and signing in works in
 * one go — a client built at module load would otherwise have bound the old choice.
 */
const switchableStorage = {
  getItem: (key: string): string | null => {
    try {
      return (getRemember() ? window.localStorage : window.sessionStorage).getItem(key);
    } catch {
      return null;
    }
  },
  setItem: (key: string, value: string): void => {
    try {
      (getRemember() ? window.localStorage : window.sessionStorage).setItem(key, value);
    } catch {
      /* Nothing to do: an unstorable session simply ends with the tab. */
    }
  },
  removeItem: (key: string): void => {
    /* Removed from BOTH. A sign-out must not leave a copy behind in the store the
       operator is no longer using, or toggling the box would resurrect a dead session. */
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* ignored */
    }
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      /* ignored */
    }
  },
};

/** `createClient`'s base: trimmed, with a trailing slash so `auth/v1` resolves beneath it. */
const BASE = new URL(SUPABASE_URL.trim().endsWith('/') ? SUPABASE_URL.trim() : `${SUPABASE_URL.trim()}/`);

/** The key `createClient` derives — MUST stay identical, or every stored session is orphaned. */
export const AUTH_STORAGE_KEY = `sb-${BASE.hostname.split('.')[0]}-auth-token`;

export const supabase: { readonly auth: GoTrueClient } = {
  auth: new AuthClient({
    url: new URL('auth/v1', BASE).href,
    headers: {
      Authorization: `Bearer ${ANON}`,
      apikey: ANON,
      'X-Client-Info': 'supabase-js/2.112.3; runtime=web',
    },
    storageKey: AUTH_STORAGE_KEY,
    flowType: 'implicit',
    persistSession: true,
    storage: switchableStorage,
    autoRefreshToken: true,
    /*
     * STILL FALSE, BUT NO LONGER FOR THE REASON THAT WAS WRITTEN HERE.
     *
     * The old note — "the API is a separate origin and reads the bearer token, so there is
     * nothing for GoTrue to do with a URL fragment here" — is true of ordinary API traffic
     * and was wrong for the mail flow. GoTrue's `GET /verify?token=…&type=invite` answers
     * 302 to `redirect_to#access_token=…&refresh_token=…&type=invite`, and with nothing
     * reading that fragment every invited user landed on the sign-in form holding a
     * credential the app had just thrown away. No invited user could ever set a password.
     *
     * The fragment is now read explicitly, in `modules/auth/auth-fragment.ts`, and the flag
     * stays off because `detectSessionInUrl` cannot do the job: it discards `type` (only
     * `recovery` survives, as a `PASSWORD_RECOVERY` event — an `invite` is
     * indistinguishable from a password sign-in), it clears the fragment by NAVIGATING so
     * the token stays one Back press away, and it swallows an expired link inside a
     * constructor that must never throw. The full argument, with line references into
     * auth-js, is in that module's header.
     */
    detectSessionInUrl: false,
  }),
};
